import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import { EmptyState } from "../../components/empty";
import { Icon } from "../../components/icon/icon";
import { AddPayoutAccountModal } from "../../components/modal/app_modals/addPayoutAccountModal";
import { Pill } from "../../components/pill";
import { MiniHeader } from "../../components/typography/miniHeader";
import { BgWrapper } from "../../components/wrapper/bgWrapper";
import { useModalControl } from "../../customeHooks/useModalControl";
import { useGetPayoutAccountQuery } from "../../redux/features/payment/payment.api";
import { SinglePayoutAccount } from "../../redux/features/payment/payment.types";
import { useAppSelector } from "../../redux/hooks";
import {
  accountIcons,
  currencyShorts,
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../utils/constants";

export const PayoutSettings = () => {
  const [selectedPayoutAccount, setSelectedPayoutAccount] =
    useState<SinglePayoutAccount>();

  const { hasPayoutAccount } = useAppSelector((state) => state.auth);

  const { modalOpen, modal, handleModalClose, handleModalOpen } =
    useModalControl();

  const {
    data: payoutAccount,
    isFetching: fetchingPayoutAccount,
    refetch,
  } = useGetPayoutAccountQuery({}, { refetchOnMountOrArgChange: true });

  return (
    <>
      {fetchingPayoutAccount ? (
        <BgWrapper className="flex flex-col gap-4">
          <Skeleton
            width={"100%"}
            height={43}
            className="rounded-2xl"
            baseColor={SKELETON_BASE_COLOR}
            highlightColor={SKELETON_HIGHLIGHT_COLOR}
          />
        </BgWrapper>
      ) : undefined}

      {(payoutAccount?.data?.length ?? 0) > 0 && !fetchingPayoutAccount ? (
        <>
          {payoutAccount?.data?.map((accnt, idx) => {
            return (
              <BgWrapper
                className="flex flex-col gap-4"
                key={`${accnt._id}-${idx}`}
              >
                <div className="flex items-center gap-3">
                  <Icon
                    svg={accountIcons[accnt.currency]}
                    width={24}
                    height={24}
                    className="text-success"
                  />

                  <div className="flex-1">
                    <MiniHeader
                      header={
                        <div className="flex w-fit items-center gap-1">
                          {accnt?.currency === "Canadian Dollars"
                            ? accnt?.interacName
                            : accnt?.accountName}
                          <Pill
                            text={currencyShorts[accnt.currency]}
                            variation="grey"
                            className="!uppercase"
                          />
                        </div>
                      }
                      subheader={`${accnt?.currency === "Canadian Dollars" ? accnt?.interacEmail : accnt?.accountNumber} ∙ ${accnt?.currency === "Canadian Dollars" ? "Interac" : accnt?.bankName}`}
                    />
                  </div>

                  <button
                    className="w-fit"
                    type="button"
                    onClick={() => {
                      setSelectedPayoutAccount(accnt);
                      handleModalOpen("addPayoutModal");
                    }}
                  >
                    <Icon
                      svg="pencil"
                      width={20}
                      height={20}
                      className="text-grey-600"
                    />
                  </button>
                </div>
              </BgWrapper>
            );
          })}

          {!Object.values(hasPayoutAccount).every((v) => v) && (
            <BgWrapper
              onClick={() => {
                handleModalOpen("addPayoutModal");
              }}
              className="flex cursor-pointer items-center gap-1 font-bold text-blue-500"
            >
              <Icon svg="plus" width={20} height={20} />
              Add payout account
            </BgWrapper>
          )}
        </>
      ) : undefined}

      {(payoutAccount?.data?.length ?? 0) <= 0 && !fetchingPayoutAccount && (
        <EmptyState
          text="No Bank Account Added"
          subtext="Add bank account as payout details"
          ctaText="Add Bank Account"
          ctaIcon=""
          onCTAClick={() => {
            handleModalOpen("addPayoutModal");
          }}
          textClassName="text-[24px]"
          subtextClassName="-mt-2"
          image="/assets/images/bank-glass.png"
        />
      )}

      <AddPayoutAccountModal
        open={modal === "addPayoutModal" && modalOpen}
        handleClose={() => {
          handleModalClose();
          setSelectedPayoutAccount(undefined);
        }}
        onSuccess={() => {
          handleModalClose();
          setSelectedPayoutAccount(undefined);
          refetch();
        }}
        mode={!!selectedPayoutAccount ? "edit" : "create"}
        account={selectedPayoutAccount}
      />
    </>
  );
};
